// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/** Small display helpers. Pure, so they are unit-tested rather than eyeballed. */

/** 128000 → "128,000". Locale-independent so tests are stable. */
export function commas(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const neg = n < 0;
  const s = Math.abs(Math.round(n)).toString();
  const out = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return neg ? `-${out}` : out;
}

/** 131072 → "128k"; 1000 → "1,000". For axis labels and chips. */
export function compactTokens(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m % 1 === 0 ? m : m.toFixed(1)}M`;
  }
  if (n >= 10_000) {
    const k = n / 1024 >= 1 && n % 1024 === 0 ? n / 1024 : n / 1000;
    return `${Math.round(k)}k`;
  }
  return commas(n);
}

export function bytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

/** 0.0731 → "7.3%". Always one decimal below 10%, none above. */
export function percent(fraction: number): string {
  if (!Number.isFinite(fraction)) return '—';
  const p = fraction * 100;
  if (p > 0 && p < 0.1) return '<0.1%';
  return `${p < 10 ? p.toFixed(1) : Math.round(p)}%`;
}

/** Collapse whitespace and clip, for table previews of long content. */
export function preview(text: string, max = 90): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  return `${flat.slice(0, max - 1)}…`;
}

/** Make control characters visible in the rendered-prompt view. */
export function visibleWhitespace(text: string): string {
  return text.replace(/\n/g, '↵\n').replace(/\t/g, '⇥');
}

export function ms(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n < 1000) return `${Math.round(n)} ms`;
  return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)} s`;
}
